import { isSupportDemoModeFromEnv } from '../config/chatSupportEnv'
import type { SupportDeskNote, SupportMessage, SupportStaffAgent, SupportTicket } from './driverSupportFirestoreService'
import type { StaffSession } from '../firebase'

/** Local QA / stakeholder demo — in-memory only, not Firestore. */
export function isSupportDemoMode(): boolean {
  return isSupportDemoModeFromEnv()
}

const STAFF_UID = 'demo_admin'
const STAFF_NAME = 'Lerato M.'

const DEMO_ONLINE_ADMINS: SupportStaffAgent[] = [
  { uid: 'demo_admin_1', name: 'Admin 1', role: 'admin', supportDeskOnline: true, lastSeenAt: new Date() },
  { uid: 'demo_admin_2', name: 'Admin 2', role: 'admin', supportDeskOnline: true, lastSeenAt: new Date() },
  { uid: 'demo_admin_3', name: 'Admin 3', role: 'admin', supportDeskOnline: true, lastSeenAt: new Date() },
  { uid: 'demo_admin_4', name: 'Admin 4', role: 'admin', supportDeskOnline: true, lastSeenAt: new Date() },
]

const presenceOnline = new Set<string>(DEMO_ONLINE_ADMINS.map((a) => a.uid))

function ago(ms: number) {
  return new Date(Date.now() - ms)
}

function seedTickets(): SupportTicket[] {
  return [
    {
      id: 'demo_q0',
      ticketNumber: 'VALT-10001',
      driverId: 'drv_p0',
      driverName: 'Anele Botha',
      driverPhone: '082 111 0001',
      userType: 'driver',
      category: 'accident',
      categoryId: 'accident',
      queueId: 'workshop',
      priority: 'P0',
      assignmentStatus: 'unassigned',
      subcategory: 'Minor collision',
      subject: 'Accident',
      description: 'Bumper scrape at robot, no injuries.',
      status: 'open',
      assignedStaffId: null,
      assignedStaffName: null,
      assignedStaffRole: null,
      vehiclePlate: 'GP 12 VALT',
      vehicleImei: '356938035643809',
      createdAt: ago(4 * 60_000),
      updatedAt: ago(90_000),
    },
    {
      id: 'demo_q1',
      ticketNumber: 'VALT-A182C3',
      driverId: 'drv_sipho',
      driverName: 'Sipho Dlamini',
      driverPhone: '082 555 0194',
      userType: 'driver',
      category: 'generalEnquiries',
      categoryId: 'generalEnquiries',
      queueId: 'finance',
      priority: 'P2',
      assignmentStatus: 'unassigned',
      subcategory: 'Payment / billing query',
      subject: 'General Enquiries',
      description: 'Wallet top-up from yesterday still not showing.',
      status: 'open',
      assignedStaffId: null,
      assignedStaffName: null,
      assignedStaffRole: null,
      vehiclePlate: 'CA 882 441',
      createdAt: ago(12 * 60_000),
      updatedAt: ago(2 * 60_000),
    },
    {
      id: 'demo_q2',
      ticketNumber: 'VALT-B90412',
      driverId: 'drv_nomsa',
      driverName: 'Nomsa Khumalo',
      driverPhone: '073 441 2208',
      userType: 'driver',
      category: 'batteryStation',
      categoryId: 'batteryStation',
      queueId: 'charging',
      priority: 'P2',
      assignmentStatus: 'unassigned',
      subcategory: 'Charger unavailable',
      subject: 'Battery / Station',
      description: 'Station near Sandton mall shows available but cable dead.',
      status: 'open',
      assignedStaffId: null,
      assignedStaffName: null,
      assignedStaffRole: null,
      createdAt: ago(25 * 60_000),
      updatedAt: ago(5 * 60_000),
    },
    {
      id: 'demo_q3',
      ticketNumber: 'VALT-C77109',
      driverId: 'drv_jabu',
      driverName: 'Jabulani Mokoena',
      driverPhone: '061 900 3341',
      userType: 'driver',
      category: 'otherSupport',
      categoryId: 'otherSupport',
      queueId: 'app',
      priority: 'P2',
      assignmentStatus: 'unassigned',
      subcategory: 'Other',
      subject: 'Other Support',
      description: 'Forced logout every ~10 minutes on Android.',
      status: 'open',
      assignedStaffId: null,
      assignedStaffName: null,
      assignedStaffRole: null,
      createdAt: ago(40 * 60_000),
      updatedAt: ago(12 * 60_000),
    },
    {
      id: 'demo_m1',
      ticketNumber: 'VALT-7F3A91',
      driverId: 'drv_thabo',
      driverName: 'Thabo Nkosi',
      driverPhone: '071 234 5678',
      userType: 'driver',
      category: 'roadAssist',
      categoryId: 'roadAssist',
      queueId: 'workshop',
      priority: 'P1',
      assignmentStatus: 'assigned',
      subcategory: 'Vehicle breakdown',
      subject: 'Road Assist',
      vehiclePlate: 'ND 440 128',
      description: 'Red battery light on dash; vehicle won’t take charge.',
      status: 'accepted',
      assignedStaffId: STAFF_UID,
      assignedStaffName: STAFF_NAME,
      assignedStaffRole: 'admin',
      createdAt: ago(55 * 60_000),
      updatedAt: ago(60_000),
      acceptedAt: ago(90_000),
    },
    {
      id: 'demo_m2',
      ticketNumber: 'VALT-D22018',
      driverId: 'drv_ayanda',
      driverName: 'Ayanda Petersen',
      driverPhone: '084 112 7780',
      userType: 'driver',
      category: 'generalEnquiries',
      categoryId: 'generalEnquiries',
      queueId: 'finance',
      priority: 'P2',
      assignmentStatus: 'assigned',
      subcategory: 'Payment / billing query',
      subject: 'General Enquiries',
      description: 'Expected R2,450; received R2,180.',
      status: 'accepted',
      assignedStaffId: STAFF_UID,
      assignedStaffName: STAFF_NAME,
      assignedStaffRole: 'admin',
      createdAt: ago(3 * 3600_000),
      updatedAt: ago(28 * 60_000),
      acceptedAt: ago(2 * 3600_000),
    },
    {
      id: 'demo_c1',
      ticketNumber: 'VALT-E44102',
      driverId: 'drv_lerato',
      driverName: 'Lindiwe Sithole',
      driverPhone: '079 555 0012',
      userType: 'driver',
      category: 'roadAssist',
      categoryId: 'roadAssist',
      queueId: 'workshop',
      priority: 'P1',
      assignmentStatus: 'assigned',
      subcategory: 'Vehicle breakdown',
      subject: 'Road Assist',
      rating: { stars: 5, tags: ['helpful'], comment: 'Sorted quickly', ratedAt: ago(6 * 3600_000) },
      description: 'Noise when stopping at traffic lights.',
      status: 'resolved',
      assignedStaffId: STAFF_UID,
      assignedStaffName: STAFF_NAME,
      assignedStaffRole: 'admin',
      createdAt: ago(2 * 86400_000),
      updatedAt: ago(6 * 3600_000),
      acceptedAt: ago(2 * 86400_000 - 3600_000),
      resolvedAt: ago(6 * 3600_000),
    },
    {
      id: 'demo_c2',
      ticketNumber: 'VALT-F88033',
      driverId: 'drv_kevin',
      driverName: 'Kevin Naidoo',
      driverPhone: '082 300 1199',
      userType: 'driver',
      category: 'otherSupport',
      categoryId: 'otherSupport',
      queueId: 'app',
      priority: 'P2',
      assignmentStatus: 'cancelled',
      subcategory: 'Other',
      subject: 'Other Support',
      description: 'Map freezes mid-trip on iOS.',
      status: 'closed',
      assignedStaffId: STAFF_UID,
      assignedStaffName: STAFF_NAME,
      assignedStaffRole: 'admin',
      createdAt: ago(4 * 86400_000),
      updatedAt: ago(1 * 86400_000),
      acceptedAt: ago(4 * 86400_000 - 7200_000),
      closedAt: ago(1 * 86400_000),
    },
    {
      id: 'demo_c3',
      ticketNumber: 'VALT-G10554',
      driverId: 'drv_zanele',
      driverName: 'Zanele Dube',
      driverPhone: '076 221 8844',
      userType: 'driver',
      category: 'generalEnquiries',
      categoryId: 'generalEnquiries',
      queueId: 'finance',
      priority: 'P2',
      assignmentStatus: 'assigned',
      subcategory: 'Documents',
      subject: 'General Enquiries',
      description: 'Cannot download last week invoice.',
      status: 'resolved',
      assignedStaffId: 'other_staff',
      assignedStaffName: 'Thabo (Finance)',
      assignedStaffRole: 'finance',
      createdAt: ago(5 * 86400_000),
      updatedAt: ago(3 * 86400_000),
      acceptedAt: ago(5 * 86400_000 - 3600_000),
      resolvedAt: ago(3 * 86400_000),
    },
  ]
}

function seedMessages(): Record<string, SupportMessage[]> {
  return {
    demo_m1: [
      {
        id: 'm1_notice',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: `${STAFF_NAME} (Admin) accepted this ticket. You can chat now.`,
        mediaType: 'text',
        createdAt: ago(85_000),
      },
      {
        id: 'm1_d1',
        senderId: 'drv_thabo',
        senderName: 'Thabo Nkosi',
        senderType: 'driver',
        text: "Hi, my vehicle isn't charging. There's a red light on the dash.",
        mediaType: 'text',
        createdAt: ago(70_000),
      },
      {
        id: 'm1_s1',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: 'Thanks Thabo — can you send a photo of the dash light?',
        mediaType: 'text',
        createdAt: ago(55_000),
      },
      {
        id: 'm1_d2',
        senderId: 'drv_thabo',
        senderName: 'Thabo Nkosi',
        senderType: 'driver',
        text: 'Yes, there is a red light here.',
        mediaType: 'image',
        mediaUrl: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=640&q=80',
        fileName: 'dash_battery.jpg',
        mimeType: 'image/jpeg',
        createdAt: ago(40_000),
      },
      {
        id: 'm1_s2',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: "Got it. We'll book you into the nearest workshop — stay on this chat.",
        mediaType: 'text',
        createdAt: ago(25_000),
      },
      {
        id: 'm1_loc',
        senderId: 'drv_thabo',
        senderName: 'Thabo Nkosi',
        senderType: 'driver',
        text: 'I am here.',
        mediaType: 'location',
        mediaUrl: 'https://maps.google.com/?q=-26.2041,28.0473',
        latitude: -26.2041,
        longitude: 28.0473,
        createdAt: ago(18_000),
      },
    ],
    demo_m2: [
      {
        id: 'm2_notice',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: `${STAFF_NAME} (Admin) accepted this ticket. You can chat now.`,
        mediaType: 'text',
        createdAt: ago(2 * 3600_000),
      },
      {
        id: 'm2_d1',
        senderId: 'drv_ayanda',
        senderName: 'Ayanda Petersen',
        senderType: 'driver',
        text: 'My weekly payout looks short by about R270.',
        mediaType: 'text',
        createdAt: ago(90 * 60_000),
      },
      {
        id: 'm2_s1',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: "I'm checking the ledger for your driver ID now.",
        mediaType: 'text',
        createdAt: ago(70 * 60_000),
      },
    ],
    demo_q1: [
      {
        id: 'q1_d1',
        senderId: 'drv_sipho',
        senderName: 'Sipho Dlamini',
        senderType: 'driver',
        text: 'I topped up R500 yesterday but my wallet still shows the old balance.',
        mediaType: 'text',
        createdAt: ago(10 * 60_000),
      },
    ],
    demo_c1: [
      {
        id: 'c1_notice',
        senderId: STAFF_UID,
        senderName: STAFF_NAME,
        senderType: 'staff',
        text: `Ticket closed by ${STAFF_NAME}. The driver can rate now.`,
        mediaType: 'text',
        createdAt: ago(6 * 3600_000),
      },
    ],
  }
}

type Listener = () => void

let tickets = seedTickets()
let messages = seedMessages()
let notes: Record<string, SupportDeskNote[]> = {
  demo_m1: [
    {
      id: 'n1',
      authorId: STAFF_UID,
      authorName: STAFF_NAME,
      text: 'Called workshop — slot open tomorrow 09:00.',
      createdAt: ago(50_000),
    },
  ],
}
const ticketListeners = new Set<Listener>()
const messageListeners = new Map<string, Set<Listener>>()
const noteListeners = new Map<string, Set<Listener>>()

function notifyTickets() {
  ticketListeners.forEach((fn) => fn())
}

function notifyMessages(ticketId: string) {
  messageListeners.get(ticketId)?.forEach((fn) => fn())
}

function notifyNotes(ticketId: string) {
  noteListeners.get(ticketId)?.forEach((fn) => fn())
}

function nextMsgId() {
  return `demo_msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
}

export const driverSupportDemoStore = {
  staffUid: STAFF_UID,
  staffName: STAFF_NAME,
  staffRole: 'admin' as const,

  listTickets(): SupportTicket[] {
    return [...tickets].sort((a, b) => (b.updatedAt?.getTime() || 0) - (a.updatedAt?.getTime() || 0))
  },

  getTicket(id: string): SupportTicket | null {
    return tickets.find((t) => t.id === id) || null
  },

  listMessages(ticketId: string): SupportMessage[] {
    return [...(messages[ticketId] || [])].sort((a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0))
  },

  watchTickets(onData: (t: SupportTicket[]) => void): () => void {
    const emit = () => onData(this.listTickets())
    ticketListeners.add(emit)
    emit()
    return () => ticketListeners.delete(emit)
  },

  watchTicket(id: string, onData: (t: SupportTicket | null) => void): () => void {
    const emit = () => onData(this.getTicket(id))
    ticketListeners.add(emit)
    emit()
    return () => ticketListeners.delete(emit)
  },

  watchMessages(ticketId: string, onData: (m: SupportMessage[]) => void): () => void {
    const emit = () => onData(this.listMessages(ticketId))
    if (!messageListeners.has(ticketId)) messageListeners.set(ticketId, new Set())
    messageListeners.get(ticketId)!.add(emit)
    emit()
    return () => messageListeners.get(ticketId)?.delete(emit)
  },

  assign(
    ticketId: string,
    staff: { uid: string; name: string; role: string } = { uid: STAFF_UID, name: STAFF_NAME, role: 'admin' },
    actor?: { staffUid: string; staffName: string },
  ) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    const prevId = t.assignedStaffId || ''
    const isTransfer = !!prevId && prevId !== staff.uid
    t.status = 'accepted'
    t.assignmentStatus = 'assigned'
    t.assignedStaffId = staff.uid
    t.assignedStaffName = staff.name
    t.assignedStaffRole = staff.role
    t.acceptedAt = now
    t.updatedAt = now
    const roleLabel = staff.role === 'finance' ? 'Finance' : staff.role === 'workshop' ? 'Workshop management' : 'Admin'
    const from = actor?.staffName || staff.name
    const text = isTransfer
      ? `${from} transferred this ticket to ${staff.name}.`
      : `${staff.name} (${roleLabel}) accepted this ticket. You can chat now.`
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: actor?.staffUid || staff.uid,
      senderName: from,
      senderType: 'staff',
      text,
      mediaType: 'text',
      createdAt: now,
    })
    notifyTickets()
    notifyMessages(ticketId)
  },

  resolve(ticketId: string, staffName = STAFF_NAME, code = 'issue_resolved', note = '', staff?: StaffSession) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    t.status = 'resolved'
    t.resolvedAt = now
    t.updatedAt = now
    t.resolutionCode = code
    t.resolutionNote = note || null
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: staff?.staffUid || STAFF_UID,
      senderName: staffName,
      senderType: 'staff',
      text: `Ticket closed by ${staffName}. The driver can rate now.`,
      mediaType: 'text',
      createdAt: now,
    })
    if (note && staff) {
      this.addNote(ticketId, `Resolution (${code}): ${note}`, staff)
    }
    notifyTickets()
    notifyMessages(ticketId)
  },

  close(ticketId: string) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    t.status = 'closed'
    t.closedAt = now
    t.updatedAt = now
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: STAFF_UID,
      senderName: STAFF_NAME,
      senderType: 'staff',
      text: 'Ticket closed.',
      mediaType: 'text',
      createdAt: now,
    })
    notifyTickets()
    notifyMessages(ticketId)
  },

  sendMessage(ticketId: string, text: string, staff = { uid: STAFF_UID, name: STAFF_NAME }) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    t.updatedAt = now
    if (!t.firstAgentResponseAt) t.firstAgentResponseAt = now
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: staff.uid,
      senderName: staff.name,
      senderType: 'staff',
      text: text.trim(),
      mediaType: 'text',
      createdAt: now,
    })
    notifyTickets()
    notifyMessages(ticketId)
  },

  uploadMedia(ticketId: string, file: File, caption: string, staff = { uid: STAFF_UID, name: STAFF_NAME }) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    t.updatedAt = now
    const mediaUrl = URL.createObjectURL(file)
    const mediaType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file'
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: staff.uid,
      senderName: staff.name,
      senderType: 'staff',
      text: caption.trim(),
      mediaType,
      mediaUrl,
      fileName: file.name,
      mimeType: file.type,
      createdAt: now,
    })
    notifyTickets()
    notifyMessages(ticketId)
  },

  listAgents(): SupportStaffAgent[] {
    const now = new Date()
    const admins = DEMO_ONLINE_ADMINS.map((a) => ({
      ...a,
      supportDeskOnline: presenceOnline.has(a.uid),
      lastSeenAt: presenceOnline.has(a.uid) ? now : null,
    }))
    return [
      ...admins,
      {
        uid: STAFF_UID,
        name: STAFF_NAME,
        role: 'admin' as const,
        supportDeskOnline: presenceOnline.has(STAFF_UID),
        lastSeenAt: presenceOnline.has(STAFF_UID) ? now : null,
      },
      { uid: 'agent_finance_1', name: 'Thabo (Finance)', role: 'finance' as const },
      { uid: 'agent_workshop_1', name: 'Nomsa (Workshop)', role: 'workshop' as const },
    ].sort((a, b) => a.uid.localeCompare(b.uid))
  },

  setPresence(staffUid: string, online: boolean) {
    if (online) presenceOnline.add(staffUid)
    else presenceOnline.delete(staffUid)
  },

  escalate(ticketId: string, staff: StaffSession) {
    const t = tickets.find((x) => x.id === ticketId)
    if (!t) throw new Error('Ticket not found')
    const now = new Date()
    t.escalated = true
    t.escalatedAt = now
    t.escalatedById = staff.staffUid
    t.escalatedByName = staff.staffName
    t.escalationTakenAt = null
    t.status = 'accepted'
    t.assignmentStatus = 'unassigned'
    t.assignedStaffId = null
    t.assignedStaffName = null
    t.assignedStaffRole = null
    t.assignedConsoleUserId = null
    t.assignedConsoleEmail = null
    t.updatedAt = now
    if (!messages[ticketId]) messages[ticketId] = []
    messages[ticketId].push({
      id: nextMsgId(),
      senderId: staff.staffUid,
      senderName: staff.staffName,
      senderType: 'staff',
      text: `${staff.staffName} escalated this ticket to Team Manager. A team manager can assign it to themselves to chat and close.`,
      mediaType: 'text',
      createdAt: now,
    })
    notifyTickets()
    notifyMessages(ticketId)
  },

  watchNotes(ticketId: string, onData: (n: SupportDeskNote[]) => void): () => void {
    const emit = () =>
      onData([...(notes[ticketId] || [])].sort((a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0)))
    if (!noteListeners.has(ticketId)) noteListeners.set(ticketId, new Set())
    noteListeners.get(ticketId)!.add(emit)
    emit()
    return () => noteListeners.get(ticketId)?.delete(emit)
  },

  addNote(ticketId: string, text: string, staff: StaffSession) {
    const now = new Date()
    if (!notes[ticketId]) notes[ticketId] = []
    notes[ticketId].push({
      id: nextMsgId(),
      authorId: staff.staffUid,
      authorName: staff.staffName,
      text,
      createdAt: now,
    })
    const t = tickets.find((x) => x.id === ticketId)
    if (t) t.updatedAt = now
    notifyTickets()
    notifyNotes(ticketId)
  },

  reset() {
    tickets = seedTickets()
    messages = seedMessages()
    notes = {}
    notifyTickets()
    messageListeners.forEach((_, id) => notifyMessages(id))
    noteListeners.forEach((_, id) => notifyNotes(id))
  },
}
